// src/components/results/barChartComponent.jsx

import React, { useEffect, useState } from "react";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Cell
} from "recharts";
import "./barChartComponent.css";

const riskColor = (title, label) => {
  const normalizedTitle = title.toLowerCase();
  const normalizedLabel = label.toLowerCase();

  //HRA
  if (normalizedTitle.includes("estado nutricional")) {
    if (normalizedLabel.includes("normal")) return "#00be9c";
    if (normalizedLabel.includes("sobrepeso")) return "#ffe951";
    if (normalizedLabel.includes("obesidad i") && !normalizedLabel.includes("ii")) return "#fd7e14"; // Naranja
    if (normalizedLabel.includes("obesidad ii") && !normalizedLabel.includes("iii")) return "#ff4848";  // Rojo claro
    if (normalizedLabel.includes("obesidad iii")) return "#dc3545"; // Rojo oscuro
    if (normalizedLabel.includes("obesidad")) return "#dc3545";
  }

  if (normalizedTitle.includes("estilo de vida")) {
    if (normalizedLabel.includes("muy saludable")) return "#00be9c";
    if (normalizedLabel.includes("saludable")) return "#096afb";
    if (normalizedLabel.includes("algo dañino")) return "#ffe951";
    if (normalizedLabel.includes("dañino")) return "#dc3545";
  }

  if (normalizedTitle.includes("actividad física")) {
    if (normalizedLabel.includes("muy activo")) return "#00be9c";
    if (normalizedLabel.includes("activo") && !normalizedLabel.includes("poco")) return "#096afb";
    if (normalizedLabel.includes("poco activo")) return "#ffe951";
    if (normalizedLabel.includes("sedentario")) return "#dc3545";
  }

  if (normalizedTitle.includes("felicidad laboral")) {
    if (normalizedLabel.includes("insatisfacción alta")) return "#dc3545";
    if (normalizedLabel.includes("satisfacción alta")) return "#00be9c";
    if (normalizedLabel.includes("inconformidad moderada")) return "#ffe951";
  }
  
  if (normalizedTitle.includes("evaluación general")) {
    if (normalizedLabel.includes("bajo")) return "#00be9c";
    if (normalizedLabel.includes("medio")) return "#ffe951";
    if (normalizedLabel.includes("alto")) return "#dc3545";

  }

  //Estrés y Burnout
  if (normalizedTitle.includes("eficiencia")) {
    if (normalizedLabel === "alta") return "#00be9c";
    if (normalizedLabel === "media") return "#ffe951";
    return "#dc3545";
  }

  if (normalizedTitle.includes("presencia")) {
    return normalizedLabel.startsWith("no ") ? "#00be9c" : "#dc3545";
  }

  if (normalizedTitle.includes("estrés físico")) {
    if (normalizedLabel.includes("sin estrés")) return "#00be9c";
    if (normalizedLabel.includes("leve")) return "#8bcf73";
    if (normalizedLabel.includes("medio")) return "#ffe951";
    if (normalizedLabel.includes("alto")) return "#fd7e14";
    return "#dc3545";
  }

  if (normalizedLabel === "bajo") return "#00be9c";
  if (normalizedLabel === "medio") return "#ffe951";
  return "#dc3545";

};

const splitLabel = (value) => {
  const words = String(value).split(" ");
  if (words.length < 2) return [value];

  let bestIndex = 1;
  let smallestDifference = Infinity;
  for (let index = 1; index < words.length; index += 1) {
    const firstLine = words.slice(0, index).join(" ");
    const secondLine = words.slice(index).join(" ");
    const difference = Math.abs(firstLine.length - secondLine.length);
    if (difference < smallestDifference) {
      bestIndex = index;
      smallestDifference = difference;
    }
  }
  return [words.slice(0, bestIndex).join(" "), words.slice(bestIndex).join(" ")];
};

function TwoLineTick({ x, y, payload }) {
  const lines = splitLabel(payload.value);
  return (
    <text x={x} y={y + 10} textAnchor="middle" fill="var(--color-azul-oscuro)" fontSize={11}>
      {lines.map((line, index) => (
        <tspan key={line} x={x} dy={index === 0 ? 0 : 14}>{line}</tspan>
      ))}
    </text>
  );
}

const fileNameFromTitle = (title) => title
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/(^-|-$)/g, "");

const BarChartComponent = ({ data, title, variant = "default", verticalLabels = false, twoLineLabels = false, semanticColors = false, downloadable = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  const handleDownload = async (event) => {
    const chartContainer = event.currentTarget.closest(".bar-chart-container");
    if (!chartContainer || downloading) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(chartContainer, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (element) => element.hasAttribute("data-download-control"),
      });
      const link = document.createElement("a");
      link.download = `${fileNameFromTitle(title) || "grafica"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("No se pudo descargar la gráfica:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`bar-chart-container ${variant === "dashboard" ? "bar-chart-container--dashboard" : ""}`}>
      <div className="bar-chart-title-row">
        <h3 className="chart-title">{title}</h3>
        {downloadable && (
          <button
            type="button"
            className="bar-chart-download-button"
            onClick={handleDownload}
            disabled={downloading}
            data-download-control
            aria-label={`Descargar ${title} en PNG`}
            title="Descargar gráfica en PNG"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12" />
              <path d="m7 10 5 5 5-5" />
              <path d="M5 21h14" />
            </svg>
            <span>{downloading ? "Generando…" : "PNG"}</span>
          </button>
        )}
      </div>
      <ResponsiveContainer width="100%" height={twoLineLabels ? 300 : (verticalLabels ? 360 : (isMobile ? 320 : 300))}>
        <BarChart
          data={data}
          barCategoryGap={20}
          margin={twoLineLabels ? { top: 8, right: 8, bottom: 42, left: 0 } : (verticalLabels ? { top: 8, right: 8, bottom: 110, left: 0 } : (isMobile ? { top: 8, right: 8, bottom: 90, left: 0 } : { bottom: 40 }))}
        >
          {/* Malla en azul claro */}
          <CartesianGrid stroke="var(--color-azul-claro)" strokeDasharray="3 3" />

          {/* Ejes en azul oscuro, forzar mostrar todas las etiquetas */}
          <XAxis
            dataKey="name"
            stroke="var(--color-azul-oscuro)"
            interval={0}
            tick={twoLineLabels ? <TwoLineTick /> : { fill: "var(--color-azul-oscuro)" }}
            angle={verticalLabels || isMobile ? -90 : 0}
            textAnchor={verticalLabels || isMobile ? "end" : "middle"}
            height={twoLineLabels ? 52 : (verticalLabels || isMobile ? 120 : 60)}
            tickMargin={verticalLabels || isMobile ? 10 : 0}
          />
          <YAxis
            stroke="var(--color-azul-oscuro)"
            tick={{ fill: "var(--color-azul-oscuro)" }}
            allowDecimals={false}
          />

          {/* Tooltip personalizado */}
          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid var(--color-gris-sec)"
            }}
            itemStyle={{ color: "var(--color-azul-oscuro)" }}
            cursor={{ fill: "rgba(0, 190, 156, 0.1)" }}
          />

          {/* Barras en delilife */}
          <Bar dataKey="value" fill="var(--color-delilife)" barSize={30}>
            {semanticColors && data.map((entry) => (
              <Cell key={entry.name} fill={riskColor(title, entry.name)} />
            ))}
            <LabelList
              dataKey="value"
              position="inside"
              fill="white"
              style={{ fontWeight: "bold" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChartComponent;
